import CustomButton from "atoms/customButton/CustomButton";
import ApiKeyTab from "component/apiKeyTab/ApiKeyTab";
import React, { useEffect, useRef } from "react";
import { Plus } from "lucide-react";
import CustomDialog from "component/CustomDialog";
import RegisterWebhook from "component/registerWebhook/RegisterWebhook";
import { useDispatch, useSelector } from "react-redux";
import { HANDLE_DELETE_WEBHOOK, HANDLE_GET_WEBHOOK_LIST, HANDLE_PATCH_WEBHOOK, HANDLE_REGISTER_WEBHOOK } from "store/webhook/action.types";
import { RootState } from "store/store";
import LoadingScreen from "component/LoadingScreen";
import { EndpointFormValues } from "interface/interface";
import CustomTableMui from "component/customTableMui/CustomTableMui";
import { WEBHOOK_HEADER } from "component/customTableMui/tableData";
import { MUI_TABLE_TYPE } from "lib/Constants";
import WarningModal from "component/warningModal/WarningModal";
import { setWebhook } from "store/webhook/reducer";
import ShowApiKey from "component/showApiKey/ShowApiKey";
import { copyToClipboard } from "lib/utils";

const WebhookCreate = () => {
	const [showModal, setShowModal] = React.useState(false);
	const dispatch = useDispatch();
	const { webhooks, loading, events, registerLoading, webhook } = useSelector((state: RootState) => state.webhook);
	const [modalTypes, setModalTypes] = React.useState<"register" | "delete">("register");
	const idRef = React.useRef<string>("");
	const selectedWebhook = useRef<Record<string, any> | null>(null);

	const handleShowModal = () => {
		setShowModal(!showModal);
	};
	const handleCopy = async () => {
		try {
			await copyToClipboard(webhook?.webhookSecret);
		} catch (error) {
			console.log(error);
		}
	};
	const handleRegister = (data: EndpointFormValues) => {
		if (selectedWebhook.current) {
			selectedWebhook.current = {
				...selectedWebhook.current,
				events: data.events,
				description: data.description,
			};
			dispatch({
				type: HANDLE_PATCH_WEBHOOK,
				payload: {
					webhookId: selectedWebhook.current.webhookId,
					data: selectedWebhook.current,
					callBack: () => {
						selectedWebhook.current = null;
						handleShowModal();
					},
				},
			});
		} else {
			dispatch({
				type: HANDLE_REGISTER_WEBHOOK,
				payload: {
					...data,
				},
			});
		}
	};

	const deleteApiKey = (id: string) => {
		setModalTypes("delete");
		idRef.current = id;
		handleShowModal();
	};
	const editWebhook = ({ id, type }: { id: string; type?: string }) => {
		dispatch(setWebhook(null));
		let webhook: any = webhooks.find((el) => el.webhookId === id);
		if (type === "toggle") {
			webhook = { ...webhook, isActive: !webhook.isActive };
			dispatch({ type: HANDLE_PATCH_WEBHOOK, payload: { webhookId: id, data: webhook } });
		} else {
			selectedWebhook.current = { ...webhook };
			handleShowModal();
			setModalTypes("register");
		}
	};

	useEffect(() => {
		dispatch({ type: HANDLE_GET_WEBHOOK_LIST });
	}, []);
	return (
		<>
			{loading ? (
				<div className="flex align-center min-h-250 justify-center h-full">
					<LoadingScreen height={false} />
				</div>
			) : (
				<>
					{webhooks?.length > 0 ? (
						<div>
							<ApiKeyTab showOverview={false} title="Webhook Endpoints" description="Create and manage webhooks to receive real-time callbacks from Swan to your external systems.">
								<CustomButton
									className="sm"
									handleFunc={() => {
										handleShowModal();
										dispatch(setWebhook(null));
										setModalTypes("register");
									}}
									buttonIcon={<Plus />}
									buttonText="Add endpoint"
								/>
							</ApiKeyTab>
							<div className="apiKeyTable">
								<CustomTableMui handleRemoveRow={deleteApiKey} columns={WEBHOOK_HEADER} rows={webhooks || []} type={MUI_TABLE_TYPE.WEBHOOK} handleEdit={editWebhook} />
							</div>
						</div>
					) : (
						<ApiKeyTab title="Webhook Endpoints" description="Create and manage webhooks to receive real-time callbacks from Swan to your external systems.">
							<CustomButton
								handleFunc={() => {
									handleShowModal();
									dispatch(setWebhook(null));
									selectedWebhook.current = null;
								}}
								buttonIcon={<Plus />}
								buttonText="Add endpoint"
							/>
						</ApiKeyTab>
					)}
				</>
			)}
			<CustomDialog open={showModal} handleShowModal={handleShowModal}>
				{webhook ? (
					<ShowApiKey
						handleShowModal={() => {
							dispatch(setWebhook(null));
							handleShowModal();
						}}
						apiKey={webhook.webhookSecret}
						handleCopy={handleCopy}
						heading="Webhook Secret"
						subheading="This is your webhook secret. Make sure to copy it before closing this dialog beacuse it will not be shown again."
					/>
				) : modalTypes === "register" ? (
					<RegisterWebhook
						handleShowModal={() => {
							selectedWebhook.current = null;
							handleShowModal();
						}}
						eventsList={events}
						loading={registerLoading}
						handleRegister={handleRegister}
						type={selectedWebhook.current ? "edit" : "create"}
						data={selectedWebhook.current}
					/>
				) : (
					<WarningModal
						message="Do you really want to delete this endpoint? No further webhooks will be sent to this endpoint. This action is irreversible."
						title="Delete Webhook"
						onClose={() => {
							handleShowModal();
							setModalTypes("register");
							selectedWebhook.current = null;
						}}
						onConfirm={() =>
							dispatch({
								type: HANDLE_DELETE_WEBHOOK,
								payload: {
									webhookId: idRef.current,
									callBack: () => {
										handleShowModal();
										setModalTypes("register");
										selectedWebhook.current = null;
									},
								},
							})
						}
						loading={registerLoading}
					/>
				)}
			</CustomDialog>
		</>
	);
};

export default WebhookCreate;
